import React from "react";
import FundtapWordmark from "../../auth/FundtapWordmark";
import "./AuthFooter.scss";

/**
 * Faithful port of the deployed marketing footer (fundtap.co "footer v7", the
 * canonical `.ftf` design), adapted for the auth surface: the menu-driven nav
 * columns (band 2) and the Get-started CTA (band 1) are removed, everything else
 * is the live design — same brand line, trust badges (Xero Connected App +
 * Matchstiq Top 100), 5.0 review cluster, fine print, and bottom bar. Badge
 * assets + social icons are the exact live ones so the auth footer flows
 * straight out of the marketing site.
 */
const XERO_BADGE =
  "https://441553093.fs1.hubspotusercontent-ap1.net/hubfs/441553093/fundtap-assets/xero-connected-app-badge-vector.svg";
const MATCHSTIQ_BADGE =
  "https://441553093.fs1.hubspotusercontent-ap1.net/hubfs/441553093/Fundtap_April2025/Images/matchstiq_Top_100_Badge_2023.webp";

const Star = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 2.2l2.9 6.03 6.6.83-4.85 4.5 1.25 6.54L12 17.9l-5.9 3.2 1.25-6.54L2.5 9.06l6.6-.83L12 2.2z" />
  </svg>
);

const AuthFooter = () => (
  <footer className="ftf">
    <div className="ftf-in">
      {/* Band 1 — brand (Get-started CTA removed) */}
      <div className="ftf-top">
        <div className="ftf-brand">
          <FundtapWordmark className="logo" />
          <p>
            On-demand invoice finance for small businesses in New Zealand and
            Australia.
          </p>
        </div>
      </div>

      {/* Band 3 — trust */}
      <div className="ftf-trust">
        <div className="ftf-trusted">
          <span>Trusted by businesses since 2010.</span>
        </div>
        <div className="ftf-cluster">
          <div className="ftf-recog">
            <a
              className="ftf-mark"
              href="https://apps.xero.com/nz/app/fundtap"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={XERO_BADGE} alt="Xero Connected App" loading="lazy" />
              <small>Xero Connected App</small>
            </a>
            <a
              className="ftf-mark"
              href="https://matchstiq.io/companies/fundtap"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={MATCHSTIQ_BADGE}
                alt="Matchstiq Top 100 Company 2023"
                loading="lazy"
              />
              <small>Matchstiq Top 100 · 2023</small>
            </a>
          </div>
          <span className="ftf-vdiv" />
          <div className="ftf-rev">
            <span className="ftf-stars" aria-hidden="true">
              <Star />
              <Star />
              <Star />
              <Star />
              <Star />
            </span>
            <span className="rate">5.0</span>
            <a
              className="exp"
              href="https://apps.xero.com/nz/app/fundtap"
              target="_blank"
              rel="noopener noreferrer"
            >
              100+ reviews on the Xero App Marketplace
            </a>
          </div>
        </div>
      </div>

      {/* Band 4 — fine print */}
      <div className="ftf-fine">
        <span className="nm">Seascape (2010) Limited</span>, trading as FundTap
        <span className="sep">·</span>NZBN 9429031726887
        <span className="sep">·</span>ABN 47 914 654 579
        <div className="ftf-priv">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <span>
            We use cookies and tracking pixels, including Meta Pixel and Google
            Analytics, to improve our site and measure our marketing. See our{" "}
            <a href="https://fundtap.co/privacy-policy">Privacy Policy</a>.
          </span>
        </div>
      </div>

      {/* Band 5 — bottom bar */}
      <div className="ftf-bot">
        <div className="links">
          <a href="https://fundtap.co/privacy-policy">Privacy</a>
          <a href="https://fundtap.co/terms-and-conditions">Terms</a>
          <a href="https://fundtap.co/contact">Contact</a>
        </div>
        <div className="right">
          <span className="ftf-social">
            <a
              href="https://www.linkedin.com/company/30923585"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="FundTap on LinkedIn"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
              </svg>
            </a>
            <a
              href="https://www.facebook.com/FundTap"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="FundTap on Facebook"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
          </span>
          <span>© {new Date().getFullYear()} Seascape (2010) Limited</span>
        </div>
      </div>
    </div>
  </footer>
);

export default AuthFooter;
